// Studio-side: create / manage client portal access (phone + password) for a lead.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { studioHeaders } from "@/lib/studioAuth";
import { useLang, tt } from "@/pages/studio/sbLang";
import { Copy, Loader2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PortalAccess({ lead, onUpdate }) {
    const lang = useLang();
    const enabled = lead.portal?.enabled;
    const [phone, setPhone] = useState(lead.portal?.phone || "");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/sales-brain/leads/${lead.id}/portal-access`,
                { phone, password }, { headers: studioHeaders() });
            onUpdate({ ...lead, portal: { phone: data.phone, enabled: true } });
            setPassword("");
            toast.success(tt(lang, "Portal access saved", "客户门户已开通 — 把链接和密码发给客户"));
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        setBusy(false);
    };
    const disable = async () => {
        await axios.delete(`${API}/sales-brain/leads/${lead.id}/portal-access`, { headers: studioHeaders() });
        onUpdate({ ...lead, portal: { ...lead.portal, enabled: false } });
        toast.success(tt(lang, "Portal disabled", "已停用客户门户"));
    };
    const copyInvite = () => {
        const url = `${window.location.origin}/portal`;
        const text = lang === "en"
            ? `Hi! Your ${lead.business_name} growth report is ready 🎉\nView it here: ${url}\nLogin phone: ${phone}\n(password sent separately)`
            : `你好！${lead.business_name} 的品牌成长报告已经准备好了 🎉\n查看链接：${url}\n登录手机号：${phone}\n（密码另发给你）`;
        navigator.clipboard.writeText(text).then(() => toast.success(tt(lang, "Invite copied", "邀请信息已复制 — 直接发给客户")));
    };

    return (
        <div data-testid="sb-portal-access">
            <p className="text-[12.5px] text-cafe-mocha mb-4">
                {tt(lang, "Give this client a phone + password to log in at /portal and view their audit score, opportunities, proposal & quote.",
                    "为客户设置手机号 + 密码，客户即可在 /portal 登录查看：健康分、成长机会、提案与报价。")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" data-testid="portal-set-phone"
                    placeholder={tt(lang, "Client phone e.g. 0123456789", "客户手机号，如 0123456789")}
                    className="rounded-xl border border-cafe-espresso/15 bg-white px-4 py-2.5 text-[13px] outline-none focus:border-cafe-olive" />
                <input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="portal-set-password"
                    placeholder={tt(lang, enabled ? "New password (reset)" : "Set password (min 6)", enabled ? "新密码（重置用）" : "设置密码（至少 6 位）")}
                    className="rounded-xl border border-cafe-espresso/15 bg-white px-4 py-2.5 text-[13px] outline-none focus:border-cafe-olive" />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
                <button onClick={save} disabled={busy || !phone || password.length < 6} data-testid="portal-save-btn"
                    className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark disabled:opacity-40 text-cafe-cream px-5 py-2 text-[12.5px] font-medium flex items-center gap-1.5">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
                    {tt(lang, enabled ? "Reset password" : "Create portal access", enabled ? "重置密码" : "开通客户门户")}
                </button>
                {enabled && (<>
                    <button onClick={copyInvite} data-testid="portal-copy-invite"
                        className="rounded-full border border-cafe-espresso/20 px-4 py-2 text-[12.5px] text-cafe-espresso flex items-center gap-1.5 hover:bg-white">
                        <Copy className="h-3.5 w-3.5" /> {tt(lang, "Copy invite message", "复制邀请信息")}</button>
                    <button onClick={disable} data-testid="portal-disable-btn"
                        className="rounded-full px-4 py-2 text-[12.5px] text-red-700/80 hover:text-red-700">
                        {tt(lang, "Disable", "停用门户")}</button>
                    <span className="text-[11px] text-cafe-olive ml-auto" data-testid="portal-status">
                        ✓ {tt(lang, `Active — login: ${lead.portal?.phone}`, `已开通 — 登录手机号 ${lead.portal?.phone}`)}</span>
                </>)}
            </div>
        </div>
    );
}
